import { useEffect, useState } from 'react';

/**
 * Dev-panel activation hook. Mirrors system-web/pages/_app.js so the
 * same `?exp-debug=1` URL contract works across both apps.
 *
 *  - `?exp-debug=1`  → enable + persist via `localStorage.expDebug = '1'`
 *  - `?exp-debug=0`  → disable + clear the localStorage flag
 *  - Vite DEV       → auto-enable on `pnpm dev`
 *  - otherwise      → enabled iff `localStorage.expDebug === '1'`
 *
 * The panel itself is lazy-loaded; this hook owns only the "should the
 * panel exist on this page load" decision.
 */
export function useExperimentDevPanelEnabled(): boolean {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(window.location.search);
      const paramValue = params.get('exp-debug');

      if (paramValue === '1') {
        window.localStorage.setItem('expDebug', '1');
        setEnabled(true);
        return;
      }
      if (paramValue === '0') {
        window.localStorage.removeItem('expDebug');
        setEnabled(false);
        return;
      }
      if (import.meta.env.DEV) {
        setEnabled(true);
        return;
      }
      if (window.localStorage.getItem('expDebug') === '1') {
        setEnabled(true);
      }
    } catch {
      // localStorage can throw in privacy modes — fail closed.
    }
  }, []);

  return enabled;
}
